import defineProjectTable, {
	Row
} from "../../../../ppi/ppi-detail/investments/project-table-definition"
import * as PpiDomain from "../../../../../domain/sfp/ppi-domain"
import * as Model from "../../../../../model"
import { forEach } from "lodash";

export function createProjectData(
	project: Model.PpiInvestmentProject,
	years: number[],
	ppi: Model.Ppi
): ProjectData {
	const rowsDef = defineProjectTable(project)
	let recetteRows: Row[];
	let induiteRows: Row[];

	recetteRows = [];
	induiteRows = [];

	return rowsDef.rows.reduce<ProjectData>((data, row) => {
		if (row.rowComponent) return data

		if (
			row.index >= rowsDef.INDUCED_RECIPE.index &&
			row.index <= rowsDef.Net_INDUCED_RECIPE.index
		  ) {
			recetteRows.push(row);
	  
			if (row.index == rowsDef.Net_INDUCED_RECIPE.index)
			  data.push(createInducesDataGroup(recetteRows, ppi, project, years));
			return data;
		  }
	  
		  if (
			row.index >= rowsDef.INDUCED_CHARGE.index &&
			row.index <= rowsDef.Net_INDUCED_CHARGE.index
		  ) {
			induiteRows.push(row);
			if (row.index == rowsDef.Net_INDUCED_CHARGE.index) {
			  data.push(createInducesDataGroup(induiteRows, ppi, project, years));
			}
			return data;
		  }

		switch (row.index) {
			case rowsDef.AMOUNT.index:
				data.push(createAmountDataRow(row, ppi, project, years))
				break
			case rowsDef.AWARDED_GRANTS.index:
			case rowsDef.ASKED_GRANTS.index:
				const awarded = row.index === rowsDef.AWARDED_GRANTS.index
				data.push(createGrantsDataGroup(row, ppi, project, years, awarded))
				break
			case rowsDef.NET_COST.index:
				data.push(createNetCostDataRow(row, ppi, project, years))
				break
			case rowsDef.FCTVA.index:
				data.push(createFctvaDataRow(row, ppi, project, years))
				break
			default: data.push(createCommonDataRow(row, ppi, project, years))
		}

		return data
	}, [])
}

export function isDataGroup(row: DataRow | DataGroup): row is DataGroup {
	return undefined !== (row as DataGroup).label
	&& undefined !== (row as DataGroup).rows
	&& undefined !== (row as DataGroup).total
}

export interface DataGroup {
	label: string
	rows: DataRow[]
	total: DataRow
}

export interface DataRow {
	budget?: number
	label: string
	rest?: number
	values?: number[]
}

export type ProjectData = (DataRow | DataGroup)[]

function createAmountDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
): DataRow {
	const budget = project.budget || 0
	const label = row.title
	const rest = budget - PpiDomain.sumProjectAmounts(project)
	const values = createDataRowValues(row, ppi, project, years)

	return { budget, label, rest, values }
}

function createCommonDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
): DataRow {
	const label = row.title
	const values = createDataRowValues(row, ppi, project, years)

	return { label, values }
}

function createDataRowValues(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
): number[] {
	let values: number[] = []

	for (const year of years) {
		if (row.func) values.push(row.func(project, year, ppi))
		else if (row.property) {
			const projectYear = PpiDomain.findProjectYearByYear(project.years, year)
			values.push(projectYear && (projectYear[row.property] || null))
		}
	}

	return values
}

function createFctvaDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
): DataRow {
	const label = row.title
	const budget = PpiDomain.getProjectFctva(ppi, project)
	const values = createDataRowValues(row, ppi, project, years)

	return { budget, label, values }
}

function createGrantDataRow(
	grant: Model.PpiInvestmentProjectGrant,
	years: number[]
): DataRow {
	const budget = grant.budget
	const label = grant.grant ? grant.grant.name : null
	const rest = budget - PpiDomain.sumGrantAmounts(grant)
	const values = PpiDomain.getGrantAmountsByYears(grant, years)

	return { budget, label, rest, values }
}

function createGrantsDataGroup(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[],
	awarded: boolean
): DataGroup {
	const label = row.title
	const grants = PpiDomain.filterGrantsByType(project.grants, awarded)
	const rows = grants.map<DataRow>(grant => createGrantDataRow(grant, years))
	const total = createGrantsTotalDataRow(row, ppi, project, years, awarded)

	return { label, rows, total }
}

function createGrantsTotalDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[],
	awarded: boolean
): DataRow {
	const label = `Total "${row.title}"`
	const budget = PpiDomain.getGrantsBudget(project, awarded)
	const rest = budget - PpiDomain.sumGrantsAmounts(project, awarded)
	const values = createDataRowValues(row, ppi, project, years)

	return { budget, label, rest, values }
}

function createNetCostDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
): DataRow {
	const label = row.title
	const budget = PpiDomain.getNetCostBudget(ppi, project, years)
	const values = createDataRowValues(row, ppi, project, years)

	return { budget, label, values }
}

function createInducesDataGroup(
	row: Row[],
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
  ): DataGroup {
	const label = row[0].title;
	let [_, ...reduceData] = row;
	reduceData.pop();
	const rows = reduceData.map<DataRow>((data) =>
	  createInduceDataRow(data, ppi, project, years)
	);
  
	let totalData = row[row.length - 1];
	const total = createInducesTotalDataRow(totalData, ppi, project, years);
  
	return { label, rows, total };
  }
  
  function createInduceDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
  ): DataRow {
	const label = row.title;
	const values = createDataRowValues(row, ppi, project, years);
	const budget = null;
	const rest = null;
  
	return { budget, label, rest, values };
  }
  
  function createInducesTotalDataRow(
	row: Row,
	ppi: Model.Ppi,
	project: Model.PpiInvestmentProject,
	years: number[]
  ): DataRow {
	const label = row.title;
	const budget = null;
	const rest = null;
	const values = createDataRowValues(row, ppi, project, years);
  
	return { budget, label, rest, values };
  }


// WEBPACK FOOTER //
// ./src/component/management-report/generator/ppi-generator/ppi-project-table/utils.ts